/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useContext, useState, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import {
  createResponseContext,
  createResponseNonPaginationContext,
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from "../../../../_mcp/helpers";
import { useQueryRequest } from "./QueryRequestProvider";
import { getUser, getUsers } from "./_request";
import { User, UsersQuery } from "./_model";

// Contexts
const QueryResponseContext = createResponseContext<User>(initialQueryResponse);
const UserDetailContext =
  createResponseNonPaginationContext<User>(initialQueryResponse);

// Provider untuk List
const QueryResponseProvider: FC<WithChildren> = ({ children }) => {
  const { state } = useQueryRequest();
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state));
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state]);

  const convertParamToObject = () => {
    const paramUrl: string = query;
    const paramToObject: any = JSON.parse(
      '{"' +
        decodeURI(paramUrl)
          .replace(/"/g, '\\"')
          .replace(/&/g, '","')
          .replace(/=/g, '":"') +
        '"}'
    );

    return paramToObject;
  };

  const removeBlankAttributes = (query: UsersQuery) => {
    return Object.fromEntries(
      Object.entries(query).filter(([_, v]) => v != null)
    );
  };

  const getObjectFilter = (query: any) => {
    const objectFilter: any = {};
    Object.keys(query).forEach(function (key) {
      if (key.includes("filter_") && key !== "filter_batch") {
        const newKey = key.replace(/^filter_/, "");
        objectFilter[newKey] = query[key];
      }
    });
    return objectFilter;
  };

  const queryPayload = () => {
    const paramObj: any = convertParamToObject();
    const filterObj = getObjectFilter(paramObj);
    const page = paramObj.page;
    const limit = paramObj.limit;
    const sort = !!paramObj.sort
      ? paramObj.order === "asc"
        ? `${paramObj.sort}`
        : `-${paramObj.sort}`
      : "-createdAt";
    const search = !!paramObj.search ? paramObj.search : null;
    const sanitizedQuery: UsersQuery = {
      paging: {
        page: page,
        limit: limit,
        sort: sort,
      },
      userName: search,
      ...filterObj,
    };
    const newQuery = removeBlankAttributes(sanitizedQuery);

    return newQuery;
  };

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery);
    }
  }, [updatedQuery]);

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.CHECK_USER_FUNDS_LIST}-${query}`,
    () => {
      const newQuery = queryPayload();
      return getUsers(newQuery);
    },
    {
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    }
  );

  return (
    <QueryResponseContext.Provider
      value={{ isLoading: isFetching, refetch, response, query }}
    >
      {children}
    </QueryResponseContext.Provider>
  );
};

// Provider untuk Detail Merchant
const UserDetailProvider: FC<WithChildren & { id: string | number }> = ({
  children,
  id,
}) => {
  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    [`${QUERIES.CHECK_USER_FUNDS_LIST}-detail`, id],
    () => getUser({ id }),
    {
      enabled: !!id,
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    }
  );

  return (
    <UserDetailContext.Provider
      value={{ isLoading: isFetching, refetch, response, query: String(id) }}
    >
      {children}
    </UserDetailContext.Provider>
  );
};

// Hooks untuk list
const useQueryResponse = () => useContext(QueryResponseContext);

const useQueryResponseData = () => {
  const { response } = useQueryResponse();
  if (!response) {
    return [];
  }
  return response.detailData.docs;
};

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    ...initialQueryState,
  };
  const { response } = useQueryResponse();
  if (!response || !response.detailData) {
    return defaultPaginationState;
  }
  const newResponse = { ...response.detailData };
  delete newResponse.docs;
  return newResponse;
};

const useQueryResponseLoading = (): boolean => {
  const { isLoading } = useQueryResponse();
  return isLoading;
};

// Hooks untuk detail
const useUserDetail = () => useContext(UserDetailContext);
const useUserDetailData = () => {
  const { response } = useUserDetail();
  return response?.detailData || null;
};
const useUserDetailLoading = (): boolean => {
  const { isLoading } = useUserDetail();
  return isLoading;
};

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
  UserDetailProvider,
  useUserDetail,
  useUserDetailData,
  useUserDetailLoading,
};
